import React, {useEffect, useMemo, useState} from 'react';
import {connect, useSelector} from 'react-redux';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {closeChooseSpecialtyModal} from './actions';
import {RootState} from '../../redux/store';
import {hdp, wdp} from '../../utils/responsive';
import {Theme} from '../../theme/lightTheme';
import TextGeneric from '../../components/textGeneric';
import {tr} from '../../translations';
import {ChooseSpecialtyModalProps} from '../../interfaces/connected-components/chooseSpecialtyModal';
import {Dimensions, FlatList, TouchableOpacity} from 'react-native';
import Config from 'react-native-config';
import Animated, {
  FadeInDown,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import Icon from '../../components/icon';
import {SpecialityType} from '../../interfaces/api/auth/login';
import {updateUser} from '../../screens/content/profile/actions';
import {Space} from '../../screens/content/home';
import {specialtyData} from '../../assets/static/dummyData';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {Platform} from 'react-native';
import '../../utils/webAnimations';

const ModalView = styled(Animated.View)`
  z-index: 0;
  position: absolute;
  top: 0;
  left: 0;
  width: ${wdp(100)}px;
  height: ${Dimensions.get('screen').height}px;
`;
const Title = styled(TextGeneric)`
  color: ${({theme}) => theme.secondary};
  font-size: ${({theme}) => theme.text.s4}px;
  text-align: left;
  padding-top: 5%;
  padding-bottom: 10%;
  width: 80%;
`;
const AnimatedTouchableOpacity =
  Animated.createAnimatedComponent(TouchableOpacity);

const ItemContainer = styled(AnimatedTouchableOpacity)<{
  isSelected: boolean;
}>`
  width: 100%;
  align-self: center;
  margin-top: 3%;
  background-color: ${({theme, isSelected}) =>
    isSelected ? theme.cardBackgroundEnd : theme.cardBackground};
  padding: 3% 4%;
  border-radius: 8px;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`;
const ItemTitle = styled(TextGeneric)`
  color: ${({theme}) => theme.secondary};
  font-size: ${({theme}) => theme.text.s6}px;
  flex: 1;
  padding-right: 5%;
`;
const IconContainer = styled.View`
  align-items: center;
  justify-content: center;
`;
const CloseIconContainer = styled.TouchableOpacity`
  background-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
  width: ${({theme}) => theme.text.s1 * 1.2}px;
  aspect-ratio: 1;
  align-items: center;
  justify-content: center;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 1;
  z-index: 99;
  position: absolute;
  top: ${({theme}) => theme.text.s4 / 2}px;
  right: 0;
`;
const screenHeight = Dimensions.get('screen').height;

const ChooseSpecialtyModal = ({
  chooseSpecialtyModal,
}: ChooseSpecialtyModalProps) => {
  const theme = useTheme() as Theme;
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {isVisible} = chooseSpecialtyModal || {};
  const {currentUser, config: userConfig} = useSelector(
    (state: RootState) => state.auth,
  );
  const [modalVisible, setModalVisible] = useState(false);
  const overlayOpacity = useSharedValue(0);
  const opacity = useSharedValue(0);
  const translateY = useSharedValue(0);

  useEffect(() => {
    overlayOpacity.value = withTiming(isVisible ? 1 : 0, {duration: 300});
    opacity.value = withTiming(isVisible ? 1 : 0, {duration: 300});
    translateY.value = withTiming(isVisible ? 1 : 0, {duration: 300});
    if (isVisible) {
      setModalVisible(true);
    } else {
      setTimeout(() => setModalVisible(false), 300);
    }
  }, [isVisible]);

  const translateStyle = useAnimatedStyle(() => {
    const translate = interpolate(translateY.value, [0, 1], [screenHeight, 0]);
    const borderRadius = interpolate(translateY.value, [0, 1], [30, 0]);
    return {
      transform: [{translateY: translate}],
      borderRadius,
      backgroundColor: `rgba(237,239,255, ${overlayOpacity.value * 0.95})`,
    };
  });

  const onItemPressHandler = (item: SpecialityType) => {
    updateUser({specialty_id: item?.id + ''});
    closeChooseSpecialtyModal();
  };

  const _renderItem = ({
    item,
    index,
  }: {
    item: SpecialityType;
    index: number;
  }) => {
    const isSelected =
      (currentUser?.email || '').trim().toLowerCase() === Config.APPLE_EMAIL
        ? currentUser?.real_specialty_id === item?.id
        : currentUser?.specialty_id === item?.id;
    return (
      <ItemContainer
        isSelected={isSelected}
        key={index}
        activeOpacity={0.8}
        onPress={() => onItemPressHandler(item)}
        {...(Platform.OS === 'web'
          ? {className: 'screen-fade-in-down'}
          : {entering: FadeInDown.delay(index * 100)})}>
        <ItemTitle>{item.name || ''}</ItemTitle>
        <IconContainer>
          <Icon
            name={'navArrow'}
            size={theme.text.s3}
            color={theme.secondary + '11'}
            stroke={theme.secondary}
          />
        </IconContainer>
      </ItemContainer>
    );
  };

  const _renderHeader = () => {
    return (
      <>
        <CloseIconContainer
          activeOpacity={0.8}
          onPress={() => closeChooseSpecialtyModal()}>
          <Icon name={'close'} size={theme.text.s55} color={theme.primary} />
        </CloseIconContainer>
        <Title>{tr('chooseYourSpeciality.title')}</Title>
      </>
    );
  };
  const _renderFooter = useMemo(
    () => <Space bottomInset={bottomInset} extraSpace={hdp(5)} />,
    [],
  );

  if (!modalVisible) {
    return null;
  }

  return (
    <ModalView style={translateStyle}>
      <FlatList
        data={[...(userConfig?.specialties || specialtyData)]}
        renderItem={_renderItem}
        ListHeaderComponent={_renderHeader}
        ListFooterComponent={_renderFooter}
        contentContainerStyle={{
          paddingHorizontal: '5%',
          flexGrow: 1,
          paddingTop: hdp(8),
        }}
        style={{
          flexGrow: 1,
        }}
      />
    </ModalView>
  );
};

const mapStateToProps = (state: RootState) => ({
  chooseSpecialtyModal: state.chooseSpecialtyModal,
});

export default connect(mapStateToProps, null)(ChooseSpecialtyModal);
